import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Input,
  Row,
  Table,
  Tag,
} from "antd";
import { TableActionHeader } from "components/Global";
import {
  AppstoreOutlined,
  CalculatorOutlined,
  InboxOutlined,
  SearchOutlined,
  StockOutlined,
  TableOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { FormEvent, ReactNode, useState } from "react";
import { getApi } from "redux/apis";
import { toastText } from "utils/utils";
import styles from "./index.module.scss";
import "./index.scss";

const USABLE_PALLET_CBM = 115200 * 0.000016387064;
const PALLET_TARE_KG = 15.88;

interface EstimateResult {
  salesOrder: {
    refNum: string;
    shipDate: string;
    shippingMethod: string | null;
    shippingAddress: Record<string, string> | null;
    status: string;
  };
  lines: Array<{
    sku: string;
    name: string;
    quantity: number;
    weightKg: number | null;
    length: number | null;
    width: number | null;
    height: number | null;
    lineWeightKg: number | null;
    lineCbm: number | null;
    missingWeight: boolean;
    missingDimensions: boolean;
    missingItemModel: boolean;
  }>;
  estimate: {
    productWeightKg: number;
    totalCbm: number;
    totalCubicInches: number;
    pallets: number;
    palletWeightKg: number;
    finalWeightKg: number;
    missing: Array<{
      sku: string;
      reasons: Array<"weight" | "dimensions" | "itemModel">;
    }>;
  };
}

type MissingReason = "weight" | "dimensions" | "itemModel";

const formatMissingSkuMessage = (reasons: MissingReason[]): string => {
  if (reasons.includes("itemModel")) {
    return "item model not assigned in Item Configuration";
  }
  const parts: string[] = [];
  if (reasons.includes("weight")) parts.push("weight");
  if (reasons.includes("dimensions")) parts.push("dimensions");
  if (parts.length === 0) return "missing data";
  if (parts.length === 1) return `missing ${parts[0]}`;
  return `missing ${parts.join(" and ")}`;
};

type ViewState = "idle" | "loading" | "notFound" | "success";

const MissingTag = () => (
  <Tag color="warning" className={styles.missingTag}>
    missing
  </Tag>
);

interface KpiCardProps {
  icon: ReactNode;
  label: string;
  value: string;
  hint: string;
  highlighted?: boolean;
}

const KpiCard = ({ icon, label, value, hint, highlighted }: KpiCardProps) => (
  <Card
    className={`${styles.kpiCard} ${highlighted ? styles.kpiCardHighlight : ""}`}
  >
    <div className={styles.kpiLabelRow}>
      <span className={styles.kpiIcon}>{icon}</span>
      <span className={styles.kpiLabel}>{label}</span>
    </div>
    <div className={styles.kpiValue}>{value}</div>
    <div className={styles.kpiHint}>{hint}</div>
  </Card>
);

const ShipmentEstimator = () => {
  const [orderNumber, setOrderNumber] = useState("");
  const [viewState, setViewState] = useState<ViewState>("idle");
  const [result, setResult] = useState<EstimateResult | null>(null);
  const [searchedOrderNumber, setSearchedOrderNumber] = useState("");

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = orderNumber.trim();
    if (!trimmed) {
      toastText("Please enter a Sales Order number", "error");
      return;
    }

    setViewState("loading");
    setResult(null);
    setSearchedOrderNumber(trimmed);

    try {
      const response = await getApi("/sales-order/estimate", {
        orderNumber: trimmed,
      });
      setResult(response?.data?.data);
      setViewState("success");
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        setViewState("notFound");
        return;
      }
      const message =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Something went wrong while estimating shipment";
      toastText(message, "error");
      setViewState("idle");
    }
  };

  const formatShippingMeta = (
    shippingMethod: string | null,
    shipDate: string | null,
  ) => {
    const method = shippingMethod?.trim() || null;
    const dateText = shipDate ? moment(shipDate).format("MM/DD/YYYY") : null;

    if (method && dateText) {
      return `${method} - ${dateText}`;
    }
    if (method) {
      return method;
    }
    if (dateText) {
      return dateText;
    }
    return "-";
  };

  const formatAddress = (address: Record<string, string> | null) => {
    if (!address) return ["-"];
    const lines: string[] = [];
    if (address.addr1) lines.push(address.addr1);
    if (address.addr2) lines.push(address.addr2);
    const cityLine = [address.city, address.state, address.postalCode]
      .filter(Boolean)
      .join(", ");
    if (cityLine) lines.push(cityLine);
    if (address.country) lines.push(address.country);
    return lines.length > 0 ? lines : ["-"];
  };

  const tableColumns = [
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      render: (text: string) => <span className={styles.monoCell}>{text}</span>,
    },
    {
      title: "Product",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right" as const,
    },
    {
      title: "Unit Wt (kg)",
      dataIndex: "weightKg",
      key: "weightKg",
      align: "right" as const,
      render: (value: number | null, record: EstimateResult["lines"][0]) =>
        record.missingItemModel || record.missingWeight ? (
          <MissingTag />
        ) : (
          value?.toFixed(2)
        ),
    },
    {
      title: "Dimensions (in)",
      key: "dimensions",
      align: "right" as const,
      render: (_: unknown, record: EstimateResult["lines"][0]) =>
        record.missingItemModel || record.missingDimensions ? (
          <MissingTag />
        ) : (
          `${record.length} × ${record.width} × ${record.height}`
        ),
    },
    {
      title: "Line Wt (kg)",
      dataIndex: "lineWeightKg",
      key: "lineWeightKg",
      align: "right" as const,
      render: (value: number | null) =>
        value != null ? value.toFixed(2) : "—",
    },
    {
      title: "Line Vol (CBM)",
      dataIndex: "lineCbm",
      key: "lineCbm",
      align: "right" as const,
      render: (value: number | null) =>
        value != null ? value.toFixed(3) : "—",
    },
  ];

  return (
    <div className={styles.wrapper}>
      <TableActionHeader title="Shipment Estimator" />
      <p className={styles.subtitle}>
        Enter a Sales Order to instantly estimate total weight, volume, and
        pallet count.
      </p>

      <Card className={styles.searchCard}>
        <form onSubmit={handleSubmit}>
          <div className={styles.searchField}>
            <label className={styles.searchLabel} htmlFor="sales-order-number">
              SALES ORDER NUMBER
            </label>
            <div className={styles.searchRow}>
              <Input
                id="sales-order-number"
                size="large"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g. 2018961"
                suffix={<SearchOutlined className={styles.searchIcon} />}
                className={styles.searchInput}
                autoFocus
              />
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={viewState === "loading"}
                icon={<CalculatorOutlined />}
                className={styles.estimateButton}
              >
                Estimate Shipment
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {viewState === "idle" && (
        <Card className={styles.emptyCard}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Enter a Sales Order number above to view the shipment estimate."
          />
        </Card>
      )}

      {viewState === "notFound" && (
        <Card className={styles.notFoundCard}>
          <div className={styles.notFoundTitle}>Sales Order not found</div>
          <div className={styles.notFoundText}>
            No order matches &quot;{searchedOrderNumber}&quot;. Please check the
            number and try again.
          </div>
        </Card>
      )}

      {viewState === "success" && result && (
        <>
          <Row gutter={[16, 16]} className={styles.kpiRow}>
            <Col xs={24} sm={12} xl={6}>
              <KpiCard
                icon={<StockOutlined />}
                label="Total Product Weight"
                value={`${result.estimate.productWeightKg.toFixed(2)} kg`}
                hint={`${result.lines.length} SKUs`}
              />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <KpiCard
                icon={<AppstoreOutlined />}
                label="Estimated Volume"
                value={`${result.estimate.totalCbm.toFixed(3)} CBM`}
                hint={`${Math.round(result.estimate.totalCubicInches).toLocaleString()} in³`}
              />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <KpiCard
                icon={<TableOutlined />}
                label="Estimated Pallets"
                value={String(result.estimate.pallets)}
                hint={`${USABLE_PALLET_CBM.toFixed(2)} CBM per pallet`}
              />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <KpiCard
                icon={<InboxOutlined />}
                label="Final Shipment Weight"
                value={`${result.estimate.finalWeightKg.toFixed(2)} kg`}
                hint={`Incl. ${result.estimate.palletWeightKg.toFixed(2)} kg pallet weight`}
              />
            </Col>
          </Row>

          {result.estimate.missing.length > 0 && (
            <Alert
              type="warning"
              showIcon
              className={styles.warningAlert}
              message="Missing product data"
              description={
                <>
                  <div>
                    The estimate may be incomplete. The following SKUs are
                    missing data:
                  </div>
                  <ul className={styles.warningList}>
                    {result.estimate.missing.map((item) => (
                      <li key={item.sku}>
                        <strong>{item.sku}</strong> —{" "}
                        {formatMissingSkuMessage(item.reasons)}
                      </li>
                    ))}
                  </ul>
                </>
              }
            />
          )}

          <Card className={styles.detailCard}>
            <div className={styles.soHeader}>
              <div>
                <div className={styles.soHeaderLabel}>Sales Order</div>
                <div className={styles.soHeaderNumber}>
                  #{result.salesOrder.refNum}
                </div>
              </div>
              <div className={styles.soHeaderMeta}>
                {formatShippingMeta(
                  result.salesOrder.shippingMethod,
                  result.salesOrder.shipDate,
                )}
              </div>
              <div className={styles.soHeaderAddress}>
                {formatAddress(result.salesOrder.shippingAddress).map(
                  (line, index) => (
                    <div key={index}>{line}</div>
                  ),
                )}
              </div>
            </div>

            <Table
              dataSource={result.lines}
              columns={tableColumns}
              rowKey={(record) => `${record.sku}-${record.name}`}
              pagination={false}
              className="table-global"
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row className={styles.summaryRow}>
                    <Table.Summary.Cell index={0} colSpan={5}>
                      Totals
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      {result.estimate.productWeightKg.toFixed(2)} kg
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      {result.estimate.totalCbm.toFixed(3)} CBM
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </Card>

          <Collapse
            className={styles.helpCollapse}
            items={[
              {
                key: "how",
                label: "How this is calculated",
                children: (
                  <div className={styles.helpContent}>
                    <p>
                      <strong>Total Weight</strong> = Σ (unit weight × qty) for
                      every SKU with weight data.
                    </p>
                    <p>
                      <strong>Volume (CBM)</strong> = Σ (L × W × H × qty) in
                      cubic inches × 0.000016387064.
                    </p>
                    <p>
                      <strong>Pallets</strong> = ⌈Total CBM ÷{" "}
                      {USABLE_PALLET_CBM.toFixed(2)}⌉, using a 48″ × 40″ pallet
                      with 60″ usable stacking height.
                    </p>
                    <p>
                      <strong>Final Weight</strong> = Product weight + (pallets
                      × {PALLET_TARE_KG} kg).
                    </p>
                  </div>
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
};

export default ShipmentEstimator;
